import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { ApiFactory } from '../api/easypos-api';

// Single hub method name the server publishes through; eventName is the
// first argument and acts as the discriminator. Mirrors RealtimeBus on the
// server (Server/EasyposServer/Realtime/RealtimeBus.cs).
const HUB_METHOD = 'event';

export type RealtimeEventHandler = (payload: unknown) => void;

export interface RealtimeConnectionStatus {
  state: HubConnectionState;
  // True once we've successfully completed at least one negotiate + connect.
  // Stays true across transient reconnects because callers usually want to
  // know "have we ever been connected this session" rather than the moment-by-
  // moment hub state.
  hasConnected: boolean;
  // Server returned 503 — AzureSignalRConnectionString is not configured.
  // Connection attempts are skipped until the token / shop selection changes.
  disabled: boolean;
  // N.7a — server returned 403 with reason="tier_requires_upgrade" on
  // negotiate. The current tier doesn't unlock realtime; HTTP path
  // continues to work and the PWA falls back to polling. Surfaced as a
  // friendly amber banner instead of a connection-failed state.
  tierBlocked?: {
    currentTier: string;
    requiredTier: string;
    message: string;
  };
  lastError?: string;
}

export type RealtimeStatusListener = (status: RealtimeConnectionStatus) => void;

// Owns the lifetime of a single SignalR HubConnection plus the API-side
// joinShopGroup / leaveShopGroup calls that determine what events the
// connection actually receives.
//
// The connection is *user-bound* (negotiate stamps the userId at issue
// time), so we recreate it when the auth token changes. Group membership
// follows the active shop; shop changes call leaveShopGroup(prev) then
// joinShopGroup(next) on the same connection.
export class RealtimeConnection {
  private _connection: HubConnection | null = null;
  private _status: RealtimeConnectionStatus = {
    state: HubConnectionState.Disconnected,
    hasConnected: false,
    disabled: false,
  };
  private readonly _eventHandlers = new Map<
    string,
    Set<RealtimeEventHandler>
  >();
  private readonly _statusListeners = new Set<RealtimeStatusListener>();
  private _currentShopName?: string;
  // Phase N device-context. The owner of this RealtimeConnection (app-index)
  // sets these once the device id is loaded and the active shop is known.
  // The negotiate request body carries them so the server can touch the
  // DeviceRegistration freshness row + (Phase O) enforce subscription
  // caps. Both are optional — older deployments / unregistered devices
  // negotiate without them.
  private _deviceId?: string;
  private _mode?: string;
  private _connecting?: Promise<void>;

  public get status(): RealtimeConnectionStatus {
    return this._status;
  }

  public get isConnected(): boolean {
    return this._connection?.state === HubConnectionState.Connected;
  }

  // Subscribe to a specific server-side eventName (e.g. "ping",
  // "menuPublished", "shopVersionUpdated"). Returns an unsubscribe function.
  public on(eventName: string, handler: RealtimeEventHandler): () => void {
    let set = this._eventHandlers.get(eventName);
    if (!set) {
      set = new Set();
      this._eventHandlers.set(eventName, set);
    }
    set.add(handler);
    return () => this.off(eventName, handler);
  }

  public off(eventName: string, handler: RealtimeEventHandler): void {
    this._eventHandlers.get(eventName)?.delete(handler);
  }

  public onStatusChange(listener: RealtimeStatusListener): () => void {
    this._statusListeners.add(listener);
    listener(this._status);
    return () => this._statusListeners.delete(listener);
  }

  // Fires when the connection transitions back to Connected after a
  // disconnect / reconnecting interval. The first connect of a session
  // does NOT fire this — pages already do their initial load on mount,
  // so we only want the "you missed events while offline, refetch"
  // signal. Returns an unsubscribe function. Pages typically use this
  // to refetch whatever their realtime listeners would have updated
  // had the events been delivered (lists, statuses, etc.).
  public onReconnected(handler: () => void): () => void {
    // Only counts as a "reconnect" if we'd previously been Connected this
    // session AND then went non-Connected. The first connect on mount
    // doesn't count — pages already load on mount.
    let droppedSinceFirstConnect = false;
    return this.onStatusChange((status) => {
      if (status.state === HubConnectionState.Connected) {
        if (droppedSinceFirstConnect) {
          droppedSinceFirstConnect = false;
          try {
            handler();
          } catch (err) {
            console.error('[realtime] reconnected handler threw', err);
          }
        }
        return;
      }
      // We only flag "dropped" once we've actually been Connected at least
      // once this session — that's the signal that subsequent non-Connected
      // states are real disconnects rather than the initial pre-connect.
      if (status.hasConnected) {
        droppedSinceFirstConnect = true;
      }
    });
  }

  // Set the device-context values that get sent on every negotiate. Safe
  // to call before / during / after a connection — values take effect on
  // the NEXT negotiate (the auto-reconnect path's accessTokenFactory will
  // pick them up). Not awaitable — pure local state.
  public setDeviceContext(opts: {
    deviceId?: string;
    mode?: string;
    shopName?: string;
  }): void {
    if (opts.deviceId !== undefined)
      this._deviceId = opts.deviceId || undefined;
    if (opts.mode !== undefined) this._mode = opts.mode || undefined;
    if (opts.shopName !== undefined)
      this._currentShopName = opts.shopName || undefined;
  }

  public get deviceId(): string | undefined {
    return this._deviceId;
  }
  public get mode(): string | undefined {
    return this._mode;
  }

  // Connect (or reconnect) using the current auth token. Idempotent — calling
  // while already connecting returns the in-flight promise; calling while
  // already connected is a no-op.
  public async connect(): Promise<void> {
    if (this._connecting) return this._connecting;
    if (this.isConnected) return;
    this._connecting = this._doConnect();
    try {
      await this._connecting;
    } finally {
      this._connecting = undefined;
    }
  }

  private async _doConnect(): Promise<void> {
    // Smoke-test the negotiate endpoint up front so we can detect a 503
    // (realtime disabled on this environment) or a 403 (tier-gated)
    // cleanly before the SignalR client tries and produces a less
    // helpful error.
    const probe = await ApiFactory.authenticated().realtimeNegotiate(
      this._negotiateBody()
    );
    if (!probe.success || !probe.data) {
      // N.7a — distinguish tier-blocked from realtime-disabled. Both
      // suppress the connection attempts (no point retrying without a
      // server-side change) but the PWA renders them differently:
      // disabled = "this environment has no realtime" (neutral chip);
      // tierBlocked = "your tier doesn't include realtime" (amber
      // upgrade banner).
      if (probe.tierBlocked) {
        this._update({
          ...this._status,
          tierBlocked: probe.tierBlocked,
          disabled: false,
          lastError: probe.tierBlocked.message,
        });
        return;
      }
      this._update({
        ...this._status,
        disabled: true,
        tierBlocked: undefined,
        lastError: probe.error ?? 'negotiate failed',
      });
      return;
    }
    // Successful negotiate clears any prior tierBlocked snapshot (the
    // owner upgraded mid-session).
    if (this._status.tierBlocked) {
      this._update({ ...this._status, tierBlocked: undefined });
    }

    const connection = new HubConnectionBuilder()
      .withUrl(probe.data.url, {
        accessTokenFactory: async () => {
          // The HubConnection caches the URL but re-fetches the access token
          // via this factory before every connect/reconnect. Keeps the token
          // fresh across token rotation and long reconnect windows.
          const negotiate = await ApiFactory.authenticated().realtimeNegotiate(
            this._negotiateBody()
          );
          if (!negotiate.success || !negotiate.data) {
            throw new Error(negotiate.error ?? 'negotiate failed');
          }
          return negotiate.data.accessToken;
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(HUB_METHOD, (eventName: string, payload: unknown) => {
      const handlers = this._eventHandlers.get(eventName);
      if (!handlers) return;
      for (const h of handlers) {
        try {
          h(payload);
        } catch (err) {
          // A throwing handler must not break delivery to the others.
          console.error(`[realtime] handler for "${eventName}" threw`, err);
        }
      }
    });

    connection.onreconnecting((err) => {
      this._update({
        ...this._status,
        state: HubConnectionState.Reconnecting,
        lastError: err?.message,
      });
    });

    connection.onreconnected(() => {
      void (async () => {
        // Re-join whichever shop we were attached to before the disconnect.
        // The server's group membership is per-user, but reconnects can leave
        // ghost group entries - re-issuing joinShopGroup is idempotent and
        // cheap.
        if (this._currentShopName) {
          await this._joinOnServer(this._currentShopName);
        }
        this._update({
          ...this._status,
          state: HubConnectionState.Connected,
          hasConnected: true,
        });
      })();
    });

    connection.onclose((err) => {
      this._update({
        ...this._status,
        state: HubConnectionState.Disconnected,
        lastError: err?.message,
      });
    });

    try {
      await connection.start();
      this._connection = connection;
      this._update({
        state: HubConnectionState.Connected,
        hasConnected: true,
        disabled: false,
      });
    } catch (err) {
      this._update({
        ...this._status,
        state: HubConnectionState.Disconnected,
        lastError: (err as Error)?.message ?? 'connect failed',
      });
    }
  }

  // Disconnect and tear down the hub. Safe to call when already disconnected.
  public async disconnect(): Promise<void> {
    const c = this._connection;
    this._connection = null;
    this._currentShopName = undefined;
    if (c) {
      try {
        await c.stop();
      } catch {
        // stop() throws on already-stopped connections in some browsers;
        // we don't care.
      }
    }
    this._update({
      state: HubConnectionState.Disconnected,
      hasConnected: this._status.hasConnected,
      disabled: this._status.disabled,
    });
  }

  // Join the named shop's group on the server. If a different shop was
  // already joined on this connection, leave that one first so the hub's
  // group membership reflects exactly the active shop.
  public async joinShop(shopName: string): Promise<void> {
    if (!this.isConnected) await this.connect();
    if (!this.isConnected) return; // connect failed (disabled, etc.)
    if (this._currentShopName && this._currentShopName !== shopName) {
      await this._leaveOnServer(this._currentShopName);
    }
    await this._joinOnServer(shopName);
    this._currentShopName = shopName;
  }

  public async leaveCurrentShop(): Promise<void> {
    if (this._currentShopName && this.isConnected) {
      await this._leaveOnServer(this._currentShopName);
    }
    this._currentShopName = undefined;
  }

  private async _joinOnServer(shopName: string): Promise<void> {
    const r = await ApiFactory.authenticated().realtimeJoinShopGroup(shopName);
    if (!r.success) {
      this._update({ ...this._status, lastError: r.error ?? 'join failed' });
    }
  }

  private async _leaveOnServer(shopName: string): Promise<void> {
    const r = await ApiFactory.authenticated().realtimeLeaveShopGroup(shopName);
    if (!r.success) {
      // Logging a leave failure is enough — the next join on a different
      // shop will overwrite group membership server-side anyway.
      console.warn('[realtime] leaveShopGroup failed:', r.error);
    }
  }

  // Builds the optional request body for /api/realtime/negotiate. Empty
  // object when no device-context has been set — the server tolerates
  // that case and behaves as before.
  private _negotiateBody() {
    return {
      shopName: this._currentShopName,
      deviceId: this._deviceId,
      mode: this._mode,
    };
  }

  private _update(next: RealtimeConnectionStatus) {
    this._status = next;
    for (const l of this._statusListeners) {
      try {
        l(next);
      } catch (err) {
        console.error('[realtime] status listener threw', err);
      }
    }
  }
}
